import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { createFileRoute } from "@tanstack/react-router";
import {
  BookOpen,
  Check,
  Copy,
  Eye,
  FolderPlus,
  GitPullRequest,
  Hand,
  KeyRound,
  ListChecks,
  MousePointerClick,
  Puzzle,
  ShieldCheck,
  SquareTerminal,
  Timer,
} from "lucide-react";
import { type ReactNode, useState } from "react";

/**
 * `/download`: lair, Hub's app for your terminal, what it does and how to install it. The
 * install command is served by this same Hub (`public/install.sh`).
 */

export const Route = createFileRoute("/_app/download")({
  component: DownloadPage,
});

const FEATURES: Array<{ icon: ReactNode; title: string; body: ReactNode }> = [
  {
    icon: <ListChecks className="h-5 w-5" />,
    title: "Plan with the planner",
    body: (
      <>
        <code>lair planner</code> opens a planning conversation about the project you're in: ideas,
        new or reworked tickets, a plan ready to build. It checks things for itself first: reads
        files, runs curl or a browser, searches the web. Everything it changes in Hub lands on the
        Planner tab with an undo.
      </>
    ),
  },
  {
    icon: <SquareTerminal className="h-5 w-5" />,
    title: "Give the developer a task",
    body: (
      <>
        <code>lair developer "add a CSV export"</code> files the ticket under your name, gives it a
        branch and opens a developer on it in a new terminal window, in a worktree of its own. The
        planner and the developer can do the same mid-conversation; <code>lair developer 1234</code>{" "}
        opens a ticket you already hold. The developer works with you; Metoděj is Hub's own agent
        that builds on the server.
      </>
    ),
  },
  {
    icon: <FolderPlus className="h-5 w-5" />,
    title: "Start a new project with the creator",
    body: (
      <>
        <code>lair creator</code> talks a brand-new project through with you: you describe it and
        hand over the materials, it asks, researches and plans it with you. Once you agree, it
        creates the project in Hub with its repo, writes the wiki and opens the releases and first
        tickets. For Hub admins.
      </>
    ),
  },
  {
    icon: <ShieldCheck className="h-5 w-5" />,
    title: "Independent audit",
    body: "Every step is checked by a fresh session that never sees how the work was described. Only an accepted step is committed, pushed and marked verified.",
  },
  {
    icon: <GitPullRequest className="h-5 w-5" />,
    title: "Merge request under your name",
    body: "When every step is verified, the app opens the merge request with your git identity and moves the ticket to its merge gate. Merging (Vratislav) and testing (Prokop) stay on Hub.",
  },
  {
    icon: <Hand className="h-5 w-5" />,
    title: "Take over and hand back",
    body: "Take a ticket's development over from Metoděj when he isn't working on it, finish it in your terminal, or hand it back: he carries on from the same branch.",
  },
  {
    icon: <MousePointerClick className="h-5 w-5" />,
    title: "Open from Hub",
    body: "Open in terminal on a ticket or on the Planner tab starts the right agent in your terminal, already in the right project.",
  },
  {
    icon: <Eye className="h-5 w-5" />,
    title: "Watch the agents",
    body: "On a ticket an agent is working on, Watch shows what it runs and says, live. Watching costs the agent nothing.",
  },
  {
    icon: <Puzzle className="h-5 w-5" />,
    title: "The team's skills",
    body: "Installs and keeps up to date the team's Claude Code skills from the netlion marketplace: Hub tickets, Grafana logs, pipelines, screenshots and more.",
  },
  {
    icon: <Timer className="h-5 w-5" />,
    title: "Time tracking (optional)",
    body: "Every ten minutes it sends your Claude Code conversations and commits to Hub, which proposes the time entries for you to approve. It counts work done outside the app too.",
  },
  {
    icon: <KeyRound className="h-5 w-5" />,
    title: "Your subscription, your name",
    body: "The agents think on your own Claude login, never on Hub's. They act as you, within the limits each agent has on the server, and everything is logged.",
  },
];

const SETUP: string[] = [
  "Asks where your project clones live (it suggests the folder with the most of them).",
  "Signs you in: Hub opens a page with a code, you check it matches and press Allow. No token to copy.",
  "Registers lair:// links so the Open in terminal buttons start your terminal (Linux and macOS).",
  "Installs the team's skills for Claude Code, or updates them.",
  "Optionally schedules the time tracker (a systemd timer on Linux, a LaunchAgent on macOS).",
];

const COMMANDS: Array<[string, string]> = [
  ["lair", "pick the agent, then the project or ticket"],
  [
    "lair planner [project]",
    "plan the project; without one, pick (this folder's repo is the default)",
  ],
  ['lair developer "<task>"', "a new ticket for this repo, a developer on it in a new window"],
  ["lair developer [ticket]", "build a ticket you hold; without one, pick from yours"],
  ["lair creator", "plan a new project together, then create it in Hub with its repo"],
  ["lair tracker sync", "send the time-tracking evidence now (it runs every ten minutes anyway)"],
  ["lair setup", "set this machine up again"],
  ["lair update", "update now; lair also updates itself in the background"],
  ["lair config projects-dir <folder>", "where your project clones live"],
];

function CopyCommand({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 p-3 font-mono text-sm">
      <span className="text-muted-foreground select-none">$</span>
      <code className="flex-1 overflow-x-auto whitespace-nowrap" data-testid="install-command">
        {command}
      </code>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          void navigator.clipboard.writeText(command).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

function DownloadPage() {
  const command = `curl -fsSL ${window.location.origin}/install.sh | sh`;
  return (
    <div className="mx-auto max-w-4xl space-y-10 py-6" data-testid="download-page">
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <SquareTerminal className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">lair</h1>
            <p className="text-muted-foreground">
              Hub's agents in your own terminal, the way you work with Claude Code. Hub is this web
              app; lair is what runs on your machine.
            </p>
          </div>
        </div>
        <CopyCommand command={command} />
        <p className="text-sm text-muted-foreground">
          Paste it into a terminal. It installs the <code>lair</code> command into{" "}
          <code>~/.local</code> and walks you through the setup; run it again any time to update.
          Needs Node.js 20+, git and{" "}
          <a
            href="https://docs.claude.com/en/docs/claude-code/setup"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Claude Code
          </a>{" "}
          signed in.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">What it does</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <Card key={f.title}>
              <CardContent className="flex gap-3 p-4">
                <div className="mt-0.5 shrink-0 text-primary">{f.icon}</div>
                <div className="space-y-1">
                  <p className="font-medium">{f.title}</p>
                  <p className="text-sm text-muted-foreground [&_code]:rounded [&_code]:bg-muted [&_code]:px-1">
                    {f.body}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="grid gap-6 md:grid-cols-2">
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">The setup</h2>
          <ol className="list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
            {SETUP.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        </div>
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Commands</h2>
          <dl className="space-y-2 text-sm">
            {COMMANDS.map(([cmd, what]) => (
              <div key={cmd}>
                <dt>
                  <code className="rounded bg-muted px-1.5 py-0.5">{cmd}</code>
                </dt>
                <dd className="mt-0.5 text-muted-foreground">{what}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <BookOpen className="h-4 w-4" />
        More in the wiki: Agenti v terminálu. The token the app gets is listed in Profile → API
        tokens, where you can revoke it.
      </p>
    </div>
  );
}
